import DineroFactory, { Dinero, Currency as DineroCurrency } from 'dinero.js';
import {abbreviateNumber, insertSeparators} from 'util/number';

export interface Currency {
  name: string;
  symbol: string;
  code: string;
  isDefault: boolean;
}

export enum CurrencyRounding {
  HalfUp,
  Down
}

export class CurrencyAmount {
  private readonly _value: Dinero;
  private readonly _currency: Currency|null;

  public get amount(): number { return this._value.getAmount() * 0.01; }
  public get currency(): Currency|null { return this._currency; }

  constructor(amount: number|Dinero, currency: Currency|null = null) {
    if(typeof(amount) === 'number') {
      this._value = DineroFactory({ amount: Math.round(amount * 100), currency: currency ? currency.code as DineroCurrency : 'GBP'});
    } else {
      this._value = amount;
    }
    this._currency = currency;
  }

  public format(format?:string) {
    if(format && format.endsWith('k')) {
      // format with abbreviations
      const abbreviatedAmount = abbreviateNumber(this.amount, false, 1);
      if(format.startsWith('$')) {
        return (this._currency?.symbol || '') + abbreviatedAmount;
      } else {
        return abbreviatedAmount;
      }
    } else {
      return this._value.toFormat(format);
    }
  }

  public static parse(val: string, currency: Currency|null = null): CurrencyAmount {
    const amount = parseFloat(val);
    if(isNaN(amount)) {
      throw new Error("Unable to parse currency: " + val);
    }
    return new CurrencyAmount(amount, currency);
  }

  public clone(): CurrencyAmount {
    return new CurrencyAmount(this.amount, this.currency);
  }

  public equals(other: CurrencyAmount): boolean {
    if(!!this._currency !== !!other._currency || (this._currency && this._currency.code !== other._currency!.code)) {
      return false;
    } else {
      return this._value.equalsTo(other._value);
    }
  }

  public add(other: CurrencyAmount): CurrencyAmount {
    if(this.currency && other.currency && this.currency.code !== other.currency.code) {
      throw new Error("Cannot add different currencies");
    }
    const newAmount = this._value.getAmount() + other._value.getAmount();
    return new CurrencyAmount(DineroFactory({ amount: newAmount, currency: this.currency?.code as DineroCurrency|undefined}), this.currency);
  }

  public subtract(other: CurrencyAmount): CurrencyAmount {
    if(this.currency && other.currency && this.currency.code !== other.currency.code) {
      throw new Error("Cannot add different currencies");
    }
    const newAmount = this._value.getAmount() - other._value.getAmount();
    return new CurrencyAmount(DineroFactory({ amount: newAmount, currency: this.currency?.code as DineroCurrency|undefined}), this.currency);
  }

  public multiply(factor: number, rounding: CurrencyRounding = CurrencyRounding.HalfUp): CurrencyAmount {
    let newAmount = this._value.getAmount() * factor;
    switch(rounding) {
      default:
      case CurrencyRounding.HalfUp:
        newAmount = Math.round(newAmount);
        break;
      case CurrencyRounding.Down:
        newAmount = Math.floor(newAmount);
        break;
    }
    return new CurrencyAmount(DineroFactory({ amount: newAmount, currency: this.currency?.code as DineroCurrency|undefined}), this.currency);
  }
}

// TODO: refactor with Dinero
export function formatCurrency(num: number, options?: { symbol?: string, decimalPlaces?: number, abbreviate?: boolean, separator?: boolean|string }) {
  const separator = options ? options.separator : undefined;
  const decimalPlaces = options ? options.decimalPlaces || 0 : 0;
  let str;
  if (options && options.abbreviate) {
    str = abbreviateNumber(num, separator);
  } else if(separator !== false) {
    str = insertSeparators(num.toFixed(decimalPlaces), typeof(separator) === 'string' ? separator : undefined);
  } else {
    str = num.toFixed(decimalPlaces);
  }
  const symbol = options ? (options.symbol || '£') : '£';
  return symbol + str;
}